// Вкладка «Банковские операции» (Stage 5, контракт §5/§6):
// приём операций от банковского монитора (webhook allpp → CRM), ручное
// сопоставление плательщику/счёту/существующему платежу, подтверждение
// платежа и распределение по строкам счёта существующими командами.
// Итоговый блок показывает только backend-значения (op.status_detail +
// launch-readiness позиции) — расчёты на frontend запрещены контрактом.
import { useState } from 'react';
import { finance, parties, requests } from '../../api/endpoints';
import {
    listBankOperations, matchBankOperation, paymentDetail, rejectBankOperation,
} from '../../api/finance-bank';
import type {
    BankOperation, Invoice, InvoiceLine, LaunchReadiness, Payment,
} from '../../api/types';
import {
    Card, Column, DataTable, ErrorBox, Field, fmtDate, fmtMoney, KV, label,
    shortId, StatusBadge,
} from '../../components/ui';
import { useAsync } from '../../hooks';

const OP_STATUSES = ['received', 'matched', 'confirmed', 'allocated', 'conflict', 'rejected'];

interface PayerChoice { id: string; name: string }

interface MatchResult {
    paymentId: string;
    invoiceId: string;
    linked: boolean;
}

function clip(value?: string | null, n = 80): string {
    if (!value) return '—';
    return value.length > n ? `${value.slice(0, n)}…` : value;
}

/** conflict_detail — JSONField: плоский объект либо строка. Рендерим читаемо. */
function fmtConflict(detail: BankOperation['conflict_detail']): string {
    if (!detail) return '';
    if (typeof detail === 'string') return detail;
    try {
        return Object.entries(detail)
            .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
            .join('; ');
    } catch {
        return JSON.stringify(detail);
    }
}

// ---------- Форма сопоставления ----------

function MatchForm({ op, payers, invoices, pendingPayments, onDone }: {
    op: BankOperation;
    payers: PayerChoice[];
    invoices: Invoice[];
    pendingPayments: Payment[];
    onDone: (res: MatchResult) => void;
}) {
    const [payerId, setPayerId] = useState('');
    const [invoiceId, setInvoiceId] = useState('');
    const [paymentId, setPaymentId] = useState('');
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    // Валюта счёта/платежа обязана совпадать с валютой операции (§5):
    // предлагаем только совместимые варианты; если таких нет — весь список.
    const sameCur = <T extends { currency?: string }>(list: T[]) =>
        op.currency ? list.filter((x) => !x.currency || x.currency === op.currency) : list;
    const invoiceOptions = sameCur(invoices);
    const paymentOptions = sameCur(pendingPayments);

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            const res = await matchBankOperation(op.id, {
                payer_id: payerId,
                invoice_id: invoiceId || undefined,
                payment_id: paymentId || undefined,
                revision: op.revision,
            });
            onDone({ paymentId: res.payment_id, invoiceId, linked: Boolean(paymentId) });
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <Card title="Сопоставление операции">
            <ErrorBox error={err} />
            <div className="form-grid">
                <Field label="Плательщик">
                    <select className="input" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
                        <option value="">— выберите —</option>
                        {payers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </Field>
                <Field label="Счёт (необязательно)"
                       hint={op.currency ? `Показаны счета в валюте операции (${op.currency})` : undefined}>
                    <select className="input" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
                        <option value="">— без счёта —</option>
                        {(invoiceOptions.length ? invoiceOptions : invoices).map((inv) => (
                            <option key={inv.id} value={inv.id}>
                                {inv.number ?? shortId(inv.id)} · {fmtMoney(inv.total, inv.currency)}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label="Существующий платёж (необязательно)"
                       hint="Только платежи в статусе «Ожидает»">
                    <select className="input" value={paymentId} onChange={(e) => setPaymentId(e.target.value)}>
                        <option value="">— создать новый платёж —</option>
                        {(paymentOptions.length ? paymentOptions : pendingPayments).map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.external_id || shortId(p.id)} · {fmtMoney(p.amount, p.currency)}
                            </option>
                        ))}
                    </select>
                </Field>
            </div>
            <div className="form-actions">
                <button className="btn primary" disabled={busy || !payerId} onClick={() => void submit()}>
                    Выполнить сопоставление
                </button>
            </div>
        </Card>
    );
}

// ---------- Платёж: подтверждение и распределение ----------

function PaymentPanel({ op, paymentId, invoiceId, onChanged }: {
    op: BankOperation;
    paymentId: string;
    invoiceId: string;
    onChanged: () => void;
}) {
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);
    const [allocLine, setAllocLine] = useState('');
    const [allocAmount, setAllocAmount] = useState('');

    // revision нужен для команд confirm/allocate — дочитываем платёж.
    const payment = useAsync<Payment | null>(() =>
        paymentDetail(paymentId).catch(async () => {
            const list = await finance.payments().catch(() => [] as Payment[]);
            return list.find((p) => p.id === paymentId) ?? null;
        }), [paymentId]);

    const lines = useAsync<InvoiceLine[]>(() =>
        (invoiceId
            ? finance.invoiceLines({ invoice: invoiceId })
                .then((ls) => (ls.length ? ls : finance.invoiceLines().catch(() => [] as InvoiceLine[])))
                .catch(() => finance.invoiceLines().catch(() => [] as InvoiceLine[]))
            : finance.invoiceLines().catch(() => [] as InvoiceLine[])),
        [invoiceId]);
    const invoiceLines = (lines.data ?? []).filter((l) => !invoiceId || l.invoice === invoiceId);

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true); setErr(null);
        try {
            await fn();
            payment.reload(); lines.reload();
            onChanged();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    const p = payment.data;
    const detailPayment = op.status_detail?.payment;

    return (
        <Card title={`Связанный платёж ${p?.external_id || shortId(paymentId)}`}
              actions={p ? <StatusBadge value={p.status} /> : undefined}>
            <ErrorBox error={payment.error} onRetry={payment.reload} />
            <ErrorBox error={err} />
            {p ? (
                <div className="kv-grid" style={{ marginBottom: 8 }}>
                    <KV k="Сумма" v={fmtMoney(p.amount, p.currency)} />
                    <KV k="Дата" v={fmtDate(p.date)} />
                    <KV k="Подтверждён" v={fmtDate(p.confirmed_at)} />
                    <KV k="Доступно к распределению"
                        v={fmtMoney(detailPayment?.payment_available, p.currency)} />
                    <KV k="Revision" v={p.revision ?? '—'} />
                </div>
            ) : null}
            <div className="form-actions">
                {p?.status === 'pending' ? (
                    <button className="btn small primary" disabled={busy}
                            onClick={() => void run(() => finance.confirmPayment(p.id, p.revision))}>
                        Подтвердить платёж
                    </button>
                ) : null}
                {p?.status === 'confirmed' ? (
                    <>
                        <Field label="Строка счёта">
                            <select className="input" value={allocLine} onChange={(e) => setAllocLine(e.target.value)}>
                                <option value="">— выберите —</option>
                                {invoiceLines.map((l) => (
                                    <option key={l.id} value={l.id}>
                                        {l.description || shortId(l.item)} · {fmtMoney(l.amount, op.currency)}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Сумма распределения">
                            <input className="input" style={{ width: 120 }} value={allocAmount}
                                   onChange={(e) => setAllocAmount(e.target.value)} />
                        </Field>
                        <button className="btn small" disabled={busy || !allocLine || !allocAmount}
                                onClick={() => void run(() => finance.allocate(
                                    p.id, p.revision,
                                    [{ invoice_line_id: allocLine, amount: allocAmount }]))}>
                            Распределить
                        </button>
                    </>
                ) : null}
            </div>
        </Card>
    );
}

// ---------- Итоги (только backend-значения) ----------

function TotalsBlock({ op, invoiceId }: { op: BankOperation; invoiceId: string }) {
    const detail = op.status_detail;
    const inv = detail?.invoice;
    const pay = detail?.payment;

    const lines = useAsync<InvoiceLine[]>(() =>
        invoiceId
            ? finance.invoiceLines({ invoice: invoiceId })
                .then((ls) => (ls.length ? ls : finance.invoiceLines().catch(() => [] as InvoiceLine[])))
                .catch(() => finance.invoiceLines().catch(() => [] as InvoiceLine[]))
            : Promise.resolve([] as InvoiceLine[]),
        [invoiceId]);
    const readinessItemId = (lines.data ?? []).find((l) => l.item)?.item ?? null;
    const readiness = useAsync<LaunchReadiness | null>(() =>
        readinessItemId ? requests.launchReadiness(readinessItemId).catch(() => null) : Promise.resolve(null),
        [readinessItemId]);

    const r = readiness.data;
    const commercial = r?.commercial;
    const cur = inv?.currency ?? pay?.currency ?? op.currency;

    if (!detail && !r) return null;

    return (
        <Card title="Итоги (данные backend)">
            <div className="kv-grid" data-testid="bank-op-totals">
                <KV k="Сумма счёта" v={inv ? fmtMoney(inv.invoice_total, inv.currency) : '—'} />
                <KV k="Распределено" v={inv ? fmtMoney(inv.paid_net, inv.currency) : '—'} />
                <KV k="Долг" v={inv ? fmtMoney(inv.debt, inv.currency) : '—'} />
                <KV k="Остаток платежа" v={pay ? fmtMoney(pay.payment_available, cur) : '—'} />
            </div>
            {r ? (
                <>
                    <div className="kv-grid" style={{ marginTop: 8 }} data-testid="bank-op-readiness">
                        <KV k="Готовность запуска"
                            v={r.can_launch ? 'Готова к запуску' : 'Не готова'} />
                        <KV k="Выставлено по позиции" v={fmtMoney(commercial?.invoiced, commercial?.currency)} />
                        <KV k="Оплачено по позиции" v={fmtMoney(commercial?.paid_net, commercial?.currency)} />
                        <KV k="Требуется" v={fmtMoney(commercial?.required, commercial?.currency)} />
                        <KV k="Не хватает" v={fmtMoney(commercial?.missing, commercial?.currency)} />
                    </div>
                    {(r.blocking_reasons ?? []).length > 0 ? (
                        <ul className="muted">
                            {r.blocking_reasons!.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                    ) : null}
                </>
            ) : null}
        </Card>
    );
}

// ---------- Детали операции ----------

function OperationDetail({ op, payers, invoices, pendingPayments, matched, onMatched, onChanged }: {
    op: BankOperation;
    payers: PayerChoice[];
    invoices: Invoice[];
    pendingPayments: Payment[];
    matched: MatchResult | null;
    onMatched: (res: MatchResult) => void;
    onChanged: () => void;
}) {
    const [showMatch, setShowMatch] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [note, setNote] = useState('');
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    const canMatch = op.status === 'received' || op.status === 'conflict';
    const canReject = op.status === 'received';
    const paymentId = op.payment ?? matched?.paymentId ?? '';
    const invoiceId = matched?.invoiceId || op.invoice || op.status_detail?.invoice?.id || '';
    const conflictText = fmtConflict(op.conflict_detail);

    const reject = async () => {
        setBusy(true); setErr(null);
        try {
            await rejectBankOperation(op.id, { note, revision: op.revision });
            setShowReject(false);
            onChanged();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <>
            <Card title={`Операция ${op.bank_operation_id || shortId(op.id)}`}
                  actions={<StatusBadge value={op.status} />}>
                <div className="kv-grid">
                    <KV k="Источник / счёт" v={`${op.source ?? '—'} · ${op.bank_account_id ?? '—'}`} />
                    <KV k="ID операции в банке" v={op.bank_operation_id ?? '—'} />
                    <KV k="Дата операции" v={fmtDate(op.operation_date)} />
                    <KV k="Дата документа" v={fmtDate(op.document_date)} />
                    <KV k="№ поручения" v={op.document_number || '—'} />
                    <KV k="Плательщик" v={op.payer_name || '—'} />
                    <KV k="Получатель" v={op.receiver_name || '—'} />
                    <KV k="Сумма" v={fmtMoney(op.amount, op.currency)} />
                    <KV k="Платёж" v={op.payment ? shortId(op.payment) : '—'} />
                    <KV k="Сопоставил" v={op.matched_by ?? '—'} />
                    <KV k="Revision" v={op.revision ?? '—'} />
                </div>
                {op.purpose ? <p className="muted" style={{ marginTop: 8 }}>{op.purpose}</p> : null}
                {conflictText ? (
                    <div className="alert alert-warn" style={{ marginTop: 8 }}>
                        <strong>Конфликт.</strong><span>{conflictText}</span>
                    </div>
                ) : null}
                {op.resolution_note ? (
                    <p className="muted" style={{ marginTop: 8 }}>Примечание: {op.resolution_note}</p>
                ) : null}
                <ErrorBox error={err} />
                <div className="form-actions">
                    {canMatch ? (
                        <button className="btn small primary"
                                onClick={() => { setShowMatch((v) => !v); setShowReject(false); }}>
                            Сопоставить
                        </button>
                    ) : null}
                    {canReject ? (
                        <button className="btn small danger"
                                onClick={() => { setShowReject((v) => !v); setShowMatch(false); }}>
                            Отклонить
                        </button>
                    ) : null}
                </div>
                {showReject && canReject ? (
                    <div className="form-actions" style={{ marginTop: 8 }}>
                        <Field label="Причина отклонения">
                            <input className="input" style={{ width: 320 }} value={note}
                                   onChange={(e) => setNote(e.target.value)} />
                        </Field>
                        <button className="btn small danger" disabled={busy || !note.trim()}
                                onClick={() => void reject()}>
                            Отклонить операцию
                        </button>
                    </div>
                ) : null}
            </Card>

            {matched ? (
                <div className="alert alert-info">
                    <strong>{matched.linked ? 'Платёж привязан.' : 'Платёж создан.'}</strong>
                    <span>Подтвердите платёж и распределите его по строкам счёта.</span>
                </div>
            ) : null}

            {showMatch && canMatch ? (
                <MatchForm op={op} payers={payers} invoices={invoices}
                           pendingPayments={pendingPayments}
                           onDone={(res) => { setShowMatch(false); onMatched(res); onChanged(); }} />
            ) : null}

            {paymentId ? (
                <PaymentPanel op={op} paymentId={paymentId} invoiceId={invoiceId}
                              onChanged={onChanged} />
            ) : null}

            <TotalsBlock op={op} invoiceId={invoiceId} />
        </>
    );
}

// ---------- Вкладка ----------

export function BankOperationsTab() {
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [matched, setMatched] = useState<MatchResult | null>(null);

    const ops = useAsync<BankOperation[]>(() =>
        listBankOperations(statusFilter ? { status: statusFilter } : undefined),
        [statusFilter]);
    const invoices = useAsync<Invoice[]>(() => finance.invoices().catch(() => []), []);
    const payments = useAsync<Payment[]>(() => finance.payments().catch(() => []), []);
    // У бухгалтера может не быть доступа к parties API — fallback на
    // минимальный справочник payer-choices (как в форме счёта/платежа).
    const payers = useAsync<PayerChoice[]>(() =>
        parties.list()
            .then((list) => list.map((p) => ({ id: p.id, name: p.name })))
            .catch(() => finance.payerChoices().catch(() => [])),
        []);

    const reload = () => { ops.reload(); invoices.reload(); payments.reload(); };
    const selected = (ops.data ?? []).find((o) => o.id === selectedId) ?? null;
    const pendingPayments = (payments.data ?? []).filter((p) => p.status === 'pending');

    const cols: Column<BankOperation>[] = [
        { key: 'source', title: 'Источник / счёт',
          render: (r) => `${r.source ?? '—'} · ${r.bank_account_id ?? '—'}` },
        { key: 'operation_date', title: 'Дата', render: (r) => fmtDate(r.operation_date) },
        { key: 'payer_name', title: 'Плательщик', render: (r) => r.payer_name || '—' },
        { key: 'amount', title: 'Сумма', render: (r) => fmtMoney(r.amount, r.currency) },
        { key: 'document_number', title: '№ поручения', render: (r) => r.document_number || '—' },
        { key: 'purpose', title: 'Назначение', render: (r) => clip(r.purpose, 60) },
        { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
        { key: 'payment', title: 'Платёж / счёт',
          render: (r) => (r.payment ? shortId(r.payment) : (r.invoice ? shortId(r.invoice) : '—')) },
        { key: 'conflict_detail', title: 'Конфликт',
          render: (r) => clip(fmtConflict(r.conflict_detail) || null, 40) },
    ];

    return (
        <>
            <Card title="Банковские операции" actions={
                <>
                    <Field label="Статус">
                        <select className="input" value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">Все</option>
                            {OP_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                        </select>
                    </Field>
                    <button className="btn small" onClick={reload}>Обновить</button>
                </>
            }>
                <ErrorBox error={ops.error} onRetry={ops.reload} />
                <DataTable columns={cols} rows={ops.data ?? []} empty="Операций нет"
                           onRowClick={(r) => {
                               setSelectedId(r.id === selectedId ? null : r.id);
                               setMatched(null);
                           }} />
            </Card>
            {selected ? (
                <OperationDetail key={selected.id} op={selected}
                                 payers={payers.data ?? []}
                                 invoices={invoices.data ?? []}
                                 pendingPayments={pendingPayments}
                                 matched={matched}
                                 onMatched={setMatched}
                                 onChanged={reload} />
            ) : null}
        </>
    );
}
